<template>
  <c-dialog
    :value="visible"
    width="850"
    close-key="cove.general.close"
    title-key="popup.caseActions.title"
    @input="close">
    <div class="pa-4" v-if="visible">
      <v-row>
        <v-col cols="12">
          <div class="c-caption">
            {{ $t('popup.caseActions.subTitle') }}
          </div>
          <div class="text-minimum-gray">
            {{ $t('popup.caseActions.subTitle.description') }}
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6" class="pa-0">
          <v-list class="pa-0 transparent">
            <action-list-item
              v-if="displayRequestCaseAccess"
              :disabled="disableRequestCaseAccess"
              key="requestCaseAccess"
              @click="requestCaseAccess"
              :title="$t('popup.caseActions.requestCaseAccess')"
              :description="$t('popup.caseActions.requestCaseAccess.description')"
              renderMarkdown
            />
            <v-divider v-if="displayRequestCaseAccess"/>
            <action-list-item
              key="createFiling"
              :to="toCreateAFiling"
              :title="$t('popup.caseActions.createFiling')"
              :description="$t('popup.caseActions.createFiling.description')"
              renderMarkdown
              :disabled="disableCreateFiling"
            />
            <v-divider v-if="displayAccessInCaseCenter"/>
            <action-list-item
              v-if="displayAccessInCaseCenter"
              key="accesInCaseCenter"
              :href="caseInstance.caseLinesURL"
              target="_blank"
              :title="$t('popup.caseActions.accessInCaseCenter')"
              :description="$t('popup.caseActions.accessInCaseCenter.description')"
              renderMarkdown
            />
          </v-list>
          <case-view-action-left-sandbox :courtID="courtID" :caseInstance="caseInstance" :isVerifiedParty="isVerifiedParty"/>
        </v-col>
        <v-divider vertical class="d-none d-md-block"/>
        <v-col cols="12" class="d-md-none pa-0">
          <v-divider class="ma-0"/>
        </v-col>
        <v-col cols="12" md="6" class="pa-0">
          <v-list class="pa-0 transparent">
            <template v-for="(filingType, i ) in filingTypes">
              <v-divider v-if="i !== 0" :key="`${filingType.portalFilingTypeID}_divider`"/>
              <action-list-item
                :key="`${filingType.portalFilingTypeID}_item`"
                :title="filingType.name"
                :description="filingType.description"
                renderMarkdown
                :disabled="disableCreateFiling"
                @click="createFiling(filingType)"
              />
            </template>
          </v-list>
          <case-view-action-right-sandbox :courtID="courtID" :caseInstance="caseInstance" :isVerifiedParty="isVerifiedParty"/>
        </v-col>
      </v-row>
    </div>
  </c-dialog>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import ActionListItem from '@components/support/ActionListItem.vue';
import CaseViewActionLeftSandbox from '@components/support/caseView/CaseViewActionsLeftSandbox.vue';
import CaseViewActionRightSandbox from '@components/support/caseView/CaseViewActionsRightSandbox.vue';
import UserAccess from '@/application/userAccess';
import { useUserStore } from '@/stores/userStore';
import { useApplicationStore } from '@/stores/applicationStore';

const userStore = useUserStore();
const applicationStore = useApplicationStore();

export default {
  components: { ActionListItem, CaseViewActionLeftSandbox, CaseViewActionRightSandbox },
  props: {
    value: {
      type: Boolean
    },
    courtID: {
      type: String
    },
    caseInstance: {
      type: Object
    },
    isVerifiedParty: {
      type: Boolean
    }
  },
  data () {
    return {
      loading: false,
      filingTypes: []
    };
  },
  computed: {
    visible () {
      return this.value && !this.loading;
    },
    isFiler () {
      return userStore.hasRole(this.CONST.ROLE_FILER);
    },
    isLoggedIn () {
      return userStore.hasSession;
    },
    disableRequestCaseAccess () {
      return !this.isLoggedIn;
    },
    displayRequestCaseAccess () {
      return !this.isVerifiedParty;
    },
    disableMakeAPayment () {
      return true;
    },
    toMakeAPayment () {
      return {
        name: 'make-payment',
        params: {
          inputData: {
            stepID: 'selectParty',
            bean: {
              caseSearchCriteria: {
                caseNumber: this.caseInstance.caseNumber
              },
              selections: {
                caseInstanceUUID: this.caseInstance.caseInstanceUUID
              },
              caseSearchResults: [ {
                caseInstanceUUID: this.caseInstance.caseInstanceUUID,
                caseNumber: this.caseInstance.caseNumber,
                caseTitle: this.caseInstance.caseTitle,
                caseClassification: this.caseInstance.caseClassification,
                filedDate: this.caseInstance.filedDate,
                closedFlag: this.caseInstance.closedFlag,
                courtID: this.caseInstance.courtID,
                courtName: applicationStore.getCourtByExternalID(this.caseInstance.courtID).displayName
              } ]
            }
          }
        }
      };
    },
    disableCreateFiling () {
      return !this.isLoggedIn || !this.isFiler;
    },
    toCreateAFiling () {
      return {
        name: 'create-filing',
        params: {
          inputData: {
            stepID: 'entryMode',
            bean: {
              courtID: this.courtID,
              caseType: this.CONST.FILING_CASE_TYPE_EXISTING,
              caseNumber: this.caseInstance.caseNumber
            }
          }
        } };
    },
    displayAccessInCaseCenter () {
      return !_.isNil(this.caseInstance.caseLinesURL);
    }
  },
  watch: {
    value (newValue) {
      if (newValue)
      {
        // prep the popup for display
        this.$cove.block();
        this.loading = true;
        this.loadFilingTypes(this.courtID, this.caseInstance.caseNumber)
          .finally(() => {
            this.loading = false;
            this.$cove.unblock();
          });
      }
      else
      {
        // reset all the data
        this.filingTypes = [];
        this.$cove.validation.clear(); // clean up any lingering validation errors if this used a form
      }
    } },
  methods: {
    loadFilingTypes (courtID, caseNumber) {
      this.loading = true;

      let url = this.$cove.getAPI({
        name: 'api.filing.types',
        params: { courtID: this.courtID },
        query: {
          filingCode: this.CONST.FILING_ENTRY_MODE_GUIDED,
          leadExistingCaseFiling: true,
          quickAction: true,
          caseNumber
        }
      });

      return axios.get(url).then((response) => {
        this.filingTypes = _.sortBy(_.defaultTo(response.data, []), 'name');
      }).finally(() => {
        this.loading = false;
      });
    },
    requestCaseAccess () {
      UserAccess.forwardToRequestAccess(this.CONST.USER_TYPE_PUBLIC, { courtID: this.courtID, caseNumber: this.caseInstance.caseNumber });
    },
    createFiling (filingType) {
      this.$cove.blockNow();

      let bean = {
        courtID: this.courtID,
        filingTypeID: filingType.portalFilingTypeID,
        caseNumber: this.caseInstance.caseNumber,
        referenceName: filingType.name
      };

      let url = this.$cove.getAPI({ name: 'api.filings' });
      axios.post(url, bean)
        .then((response) => {
          let filingID = response.data.resourceID;
          this.$router.push({ name: 'manage-filing', params: { filingID } });
          this.$cove.notify({ message: this.$t('application.save.success'), color: 'success' });
        })
        .finally(() => {
          this.$cove.unblock();
        });
    },
    close () {
      this.$emit('input', false);
    }
  }
};
</script>